import { RemoteData } from '../../reducers/remoteData';
import { DateAsString, SporsmalType, SvarId, VerkstedWebDTO } from '../../types/generated-types';
import { Kundeinfo } from '../../types/internal-types';
import {
    toUploadedVedleggDTO,
    UploadedVedlegg,
    UploadedVedleggResult,
    Vedlegg,
    VedleggResultStatus,
    VedleggWithFile,
} from '../filopplasting/filopplasting-types';
import {
    CurrentForsikringObjekterCheckboxSporsmal,
    ObjektAlternativ,
} from '../forsikring-objekter-checkbox/forsikring-objekter-checkbox-types';
import { Kontaktinfofelt } from '../kontaktinfo/kontaktinfofelt';
import { InputGroup } from '../kredittkortnummer/kredittkortnummer-types';
import { SimpleKontaktinfofelt } from '../simpleKontaktinfo/simpleKontaktinfofelt';
import { UpdateUtleggslinjeData } from '../utlegg/utlegg';
import { BesvartSporsmal, CurrentSporsmal } from './current-steg';
import { CurrentSporsmalAction } from './currentSporsmalReducer';
import { Bilmerke, ForsikringAlternativ } from './sporsmalsflyt-types';

export enum SporsmalsflytActionKeys {
    UpdateSvarTekst = 'UPDATE_SVAR',
    UpdateSvarId = 'UPDATE_SVARID',
    UpdateDatoSvar = 'UPDATE_DATO_SVAR',
    UpdateSkadedatoInSkadedatoAndForsikringSvar = 'UPDATE_SKADEDATO_SVAR',
    UpdateKontaktinfosvar = 'UPDATE_KONTAKTINFOSVAR',
    UpdateKontaktInfoInvolvertSvar = 'UPDATE_KONTAKTINFOINVOLVERT_SVAR',
    UpdateSimpleKontaktinfosvar = 'UPDATE_SIMPLE_KONTAKTINFOSVAR',
    UpdateSkadelidtInfoSvar = 'UPDATE_SKADELIDT_INFO_SVAR',
    UpdateSkadelidtInfoBankIdOgMobilSvar = 'UPDATE_SKADE_LIDTE_INFO_BANK_ID_OG_MOBIL_SVAR',
    UpdateUtleggslinje = 'UPDATE_UTLEGGSLINJE',
    UpdateUtleggslinjeDato = 'UPDATE_UTLEGGSLINJE_DATO',
    UpdateForsikringSvar = 'UPDATE_FORSIKRING_SVAR',
    UpdateForsikringInSkadedatoAndForsikringSvar = 'UPDATE_FORSIKRING_IN_SKADEDATO_AND_FORSIKRINGSVAR',
    AddUtleggslinje = 'ADD_UTLEGGSLINJE',
    AddVedleggToSvar = 'ADD_VEDLEGG_TO_SVAR',
    AddDirectVedleggToSvar = 'ADD_DIRECT_VEDLEGG_TO_SVAR',
    DeleteVedleggFromSvar = 'DELETE_VEDLEGG_FROM_SVAR',
    UpdatePostnummer = 'UPDATE_POSTNUMMER',
    UpdatePostnummerAndPoststed = 'UPDATE_POSTNUMMER_AND_POSTSTED',
    UpdateBilmerke = 'UPDATE_BILMERKE',
    UpdateFetchVerkstederStatus = 'UPDATE_FETCH_VERKSTEDER_STATUS',
    UpdateSporsmalSide = 'UPDATE_SPORSMAL_SIDE',
    UpdateSporsmalList = 'UPDATE_SPORSMAL_LIST',
    AddSporsmalListSporsmalsflytCollection = 'ADD_SPORSMAL_LIST_SPORSMALSFLYT_COLLECTION',
    RemoveSporsmalListSporsmalsflytCollection = 'Remove_Sporsmal_List_Sporsmalsflyt_Collection',
    ToggleSporsmalListItem = 'Toggle_Sporsmal_List_Item',
    NextStep = 'NEXT_STEP',
    PreviousStep = 'PREVIOUS_STEP',
    UpdateUploadVedlegg = 'UPDATE_UPLOAD_VEDLEGG',
    UpdateWithBesvarteSporsmalFromCacheAction = 'UPDATE_WITH_BESVARTE_SPORSMAL_FROM_CACHE',
    UpdateForsikringObjekterValg = 'UPDATE_FORSIKRING_OBJEKTER_VALG',
    UpdateForsikringObjekterCheckboxWithObjekter = 'UPDATE_FORSIKRING_OBJEKTER_CHECKBOX_WITH_OBJEKTER',
    UpdateKredittkortnummer = 'UPDATE_KREDITTKORTNUMMER',
}

export interface UpdateKredittkortnummerAction {
    type: SporsmalsflytActionKeys.UpdateKredittkortnummer;
    updatedSvar: string;
    inputGroup: InputGroup;
}

export const updateKredittkortnummer = (
    updatedSvar: string,
    inputGroup: InputGroup,
): UpdateKredittkortnummerAction => ({
    type: SporsmalsflytActionKeys.UpdateKredittkortnummer,
    updatedSvar,
    inputGroup,
});

export interface UpdateSvarAction {
    type: SporsmalsflytActionKeys.UpdateSvarTekst;
    svarTekst: string;
}

export const updateSvar = (svarTekst: string): UpdateSvarAction => ({
    type: SporsmalsflytActionKeys.UpdateSvarTekst,
    svarTekst,
});

export interface UpdateSvarIdAction {
    type: SporsmalsflytActionKeys.UpdateSvarId;
    svarId: SvarId;
}

export const updateSvarId = (svarId: SvarId): UpdateSvarIdAction => ({
    type: SporsmalsflytActionKeys.UpdateSvarId,
    svarId,
});

export interface UpdateSporsmalSideAction {
    type: SporsmalsflytActionKeys.UpdateSporsmalSide;
    sporsmalId: string;
    action: CurrentSporsmalAction;
}

export const updateSporsmalSide = (action: CurrentSporsmalAction, sporsmalId: string): UpdateSporsmalSideAction => ({
    type: SporsmalsflytActionKeys.UpdateSporsmalSide,
    sporsmalId,
    action,
});

export interface UpdateSporsmalListAction {
    type: SporsmalsflytActionKeys.UpdateSporsmalList;
    sporsmalCollectionId: string;
    action: UpdateSporsmalSideAction;
}

export const updateSporsmalList = (
    sporsmalCollectionId: string,
    sporsmalId: string,
    action: CurrentSporsmalAction,
): UpdateSporsmalListAction => ({
    type: SporsmalsflytActionKeys.UpdateSporsmalList,
    sporsmalCollectionId,
    action: updateSporsmalSide(action, sporsmalId),
});

export interface AddSporsmalListSporsmalsflytCollection {
    type: SporsmalsflytActionKeys.AddSporsmalListSporsmalsflytCollection;
}

export const addSporsmalListSporsmalsflytCollection = (): AddSporsmalListSporsmalsflytCollection => ({
    type: SporsmalsflytActionKeys.AddSporsmalListSporsmalsflytCollection,
});

export interface RemoveSporsmalListSporsmalsflytCollection {
    type: SporsmalsflytActionKeys.RemoveSporsmalListSporsmalsflytCollection;
    id: string;
}

export const removeSporsmalListSporsmalsflytCollection = (id: string): RemoveSporsmalListSporsmalsflytCollection => ({
    type: SporsmalsflytActionKeys.RemoveSporsmalListSporsmalsflytCollection,
    id,
});

export interface ToggleSporsmalListItem {
    type: SporsmalsflytActionKeys.ToggleSporsmalListItem;
    id: string;
}

export const toggleSporsmalListItem = (id: string) => ({
    type: SporsmalsflytActionKeys.ToggleSporsmalListItem,
    id,
});

export interface UpdateForsikringSvarAction {
    type: SporsmalsflytActionKeys.UpdateForsikringSvar;
    selectedAlternativ: ForsikringAlternativ;
}

export const updateForsikringSvar = (selectedAlternativ?: ForsikringAlternativ): UpdateForsikringSvarAction => ({
    type: SporsmalsflytActionKeys.UpdateForsikringSvar,
    selectedAlternativ,
});

export interface UpdateForsikringInSkadedatoAndForsikringSvarAction {
    type: SporsmalsflytActionKeys.UpdateForsikringInSkadedatoAndForsikringSvar;
    selectedAlternativ: ForsikringAlternativ;
}

export const updateForsikringInSkadedatoAndForsikringSvar = (
    selectedAlternativ: ForsikringAlternativ,
): UpdateForsikringInSkadedatoAndForsikringSvarAction => ({
    type: SporsmalsflytActionKeys.UpdateForsikringInSkadedatoAndForsikringSvar,
    selectedAlternativ,
});

export interface UpdateDatoSvarAction {
    type: SporsmalsflytActionKeys.UpdateDatoSvar;
    dato: DateAsString;
}

export const updateDatoSvar = (dato: DateAsString): UpdateDatoSvarAction => ({
    type: SporsmalsflytActionKeys.UpdateDatoSvar,
    dato,
});

export interface UpdateSkadedatoSvarAction {
    type: SporsmalsflytActionKeys.UpdateSkadedatoInSkadedatoAndForsikringSvar;
    skadedato: DateAsString;
    kundeinfo: Kundeinfo;
}

export const updateSkadedatoInSkadedatoAndForsikringSvar = (
    skadedato: DateAsString,
    kundeinfo: Kundeinfo,
): UpdateSkadedatoSvarAction => ({
    type: SporsmalsflytActionKeys.UpdateSkadedatoInSkadedatoAndForsikringSvar,
    skadedato,
    kundeinfo,
});

export interface UpdateKontaktinfosvarAction {
    type: SporsmalsflytActionKeys.UpdateKontaktinfosvar;
    felt: Kontaktinfofelt;
    svarTekst: string;
}

export interface UpdateSkadelidtInfoSvarAction {
    type: SporsmalsflytActionKeys.UpdateSkadelidtInfoSvar;
    field: Kontaktinfofelt;
    svar: string;
}

export interface UpdateSkadelidtInfoBankIdOgMobilSvarAction {
    type: SporsmalsflytActionKeys.UpdateSkadelidtInfoBankIdOgMobilSvar;
    svar: boolean;
}

export interface UpdateKontaktInfoIvolvertSvarAction {
    type: SporsmalsflytActionKeys.UpdateKontaktInfoInvolvertSvar;
    felt: Kontaktinfofelt;
    svarTekst: string;
}

export interface UpdateSimpleKontaktinfosvarAction {
    type: SporsmalsflytActionKeys.UpdateSimpleKontaktinfosvar;
    felt: SimpleKontaktinfofelt;
    svarTekst: string;
}

export const updateKontaktinfosvar = (felt: Kontaktinfofelt, svarTekst: string): UpdateKontaktinfosvarAction => ({
    type: SporsmalsflytActionKeys.UpdateKontaktinfosvar,
    felt,
    svarTekst,
});

export const updateSkadelidtInfoSvar = (field: Kontaktinfofelt, svar: string): UpdateSkadelidtInfoSvarAction => ({
    type: SporsmalsflytActionKeys.UpdateSkadelidtInfoSvar,
    field,
    svar,
});

export const updateSkadelidtInfoHasBankIdandMobilSvar = (
    svar: boolean,
): UpdateSkadelidtInfoBankIdOgMobilSvarAction => ({
    type: SporsmalsflytActionKeys.UpdateSkadelidtInfoBankIdOgMobilSvar,
    svar,
});

export const updateKontaktInfoInvolvertSvar = (
    felt: Kontaktinfofelt,
    svarTekst: string,
): UpdateKontaktInfoIvolvertSvarAction => ({
    type: SporsmalsflytActionKeys.UpdateKontaktInfoInvolvertSvar,
    felt,
    svarTekst,
});

export const updateSimpleKontaktinfosvar = (
    felt: SimpleKontaktinfofelt,
    svarTekst: string,
): UpdateSimpleKontaktinfosvarAction => ({
    type: SporsmalsflytActionKeys.UpdateSimpleKontaktinfosvar,
    felt,
    svarTekst,
});

export interface UpdateUtleggslinjeAction {
    type: SporsmalsflytActionKeys.UpdateUtleggslinje;
    utleggslinje: UpdateUtleggslinjeData;
}

export const updateUtleggslinje = (utleggslinje: UpdateUtleggslinjeData): UpdateUtleggslinjeAction => ({
    type: SporsmalsflytActionKeys.UpdateUtleggslinje,
    utleggslinje,
});

export interface UpdateUtleggslinjeDatoAction {
    type: SporsmalsflytActionKeys.UpdateUtleggslinjeDato;
    utleggslinjeId: number;
    dato: DateAsString;
}

export const updateUtleggslinjeDato = (utleggslinjeId: number, dato: DateAsString): UpdateUtleggslinjeDatoAction => ({
    type: SporsmalsflytActionKeys.UpdateUtleggslinjeDato,
    utleggslinjeId,
    dato,
});

export interface AddUtleggslinjeAction {
    type: SporsmalsflytActionKeys.AddUtleggslinje;
}

export const addUtleggslinje = (): AddUtleggslinjeAction => ({
    type: SporsmalsflytActionKeys.AddUtleggslinje,
});

export interface AddVedleggToSvarAction {
    type: SporsmalsflytActionKeys.AddVedleggToSvar;
    vedlegg: Vedlegg[];
}

export const addVedleggToSvar = (vedlegg: Vedlegg[]): AddVedleggToSvarAction => ({
    type: SporsmalsflytActionKeys.AddVedleggToSvar,
    vedlegg,
});

export interface AddDirectVedleggToSvarAction {
    type: SporsmalsflytActionKeys.AddDirectVedleggToSvar;
    vedlegg: Vedlegg[];
    status: VedleggResultStatus;
}

export const addDirectVedleggToSvar = (
    vedlegg: Vedlegg[],
    status: VedleggResultStatus,
): AddDirectVedleggToSvarAction => ({
    type: SporsmalsflytActionKeys.AddDirectVedleggToSvar,
    vedlegg,
    status,
});

export interface DeleteVedleggFromSvarAction {
    type: SporsmalsflytActionKeys.DeleteVedleggFromSvar;
    vedlegg: Vedlegg[];
}

export const deleteVedleggFromSvar = (vedlegg: Vedlegg[]): DeleteVedleggFromSvarAction => ({
    type: SporsmalsflytActionKeys.DeleteVedleggFromSvar,
    vedlegg,
});

export interface UpdatePostnummerAction {
    type: SporsmalsflytActionKeys.UpdatePostnummer;
    postnummer: string;
}

export const updatePostnummer = (postnummer: string): UpdatePostnummerAction => ({
    type: SporsmalsflytActionKeys.UpdatePostnummer,
    postnummer,
});

export interface UpdatePostnummerAndPoststedAction {
    type: SporsmalsflytActionKeys.UpdatePostnummerAndPoststed;
    postnummer: string;
    poststed: RemoteData<string>;
}

export const updatePostnummerAndPoststed = (
    postnummer: string,
    poststed: RemoteData<string>,
): UpdatePostnummerAndPoststedAction => ({
    type: SporsmalsflytActionKeys.UpdatePostnummerAndPoststed,
    postnummer,
    poststed,
});

export interface UpdateBilmerkeAction {
    type: SporsmalsflytActionKeys.UpdateBilmerke;
    bilmerke?: Bilmerke;
}

export const updateBilmerke = (bilmerke?: Bilmerke): UpdateBilmerkeAction => ({
    type: SporsmalsflytActionKeys.UpdateBilmerke,
    bilmerke,
});

export interface UpdateFetchVerkstederStatusAction {
    type: SporsmalsflytActionKeys.UpdateFetchVerkstederStatus;
    postnummer: string;
    bilmerke: Bilmerke;
    verksteder: RemoteData<VerkstedWebDTO[]>;
}

export const updateFetchVerkstederStatus = (
    verksteder: RemoteData<VerkstedWebDTO[]>,
    postnummer: string,
    bilmerke: Bilmerke,
): UpdateFetchVerkstederStatusAction => ({
    type: SporsmalsflytActionKeys.UpdateFetchVerkstederStatus,
    verksteder,
    postnummer,
    bilmerke,
});

export interface UpdateUploadedVedleggAction {
    type: SporsmalsflytActionKeys.UpdateUploadVedlegg;
    result: UploadedVedleggResult;
    status: VedleggResultStatus;
}

export const updateUploadedVedlegg = (
    result: UploadedVedleggResult,
    status: VedleggResultStatus,
): UpdateUploadedVedleggAction => ({
    type: SporsmalsflytActionKeys.UpdateUploadVedlegg,
    result,
    status,
});

export const buildVedleggFormData = (vedlegg: VedleggWithFile[], existingids: UploadedVedlegg): FormData => {
    const formData = new FormData();
    const vedleggDTO = toUploadedVedleggDTO(existingids);

    formData.append(
        'UploadedVedleggDTO'.toLowerCase(),
        new Blob([JSON.stringify(vedleggDTO)], { type: 'application/json' }),
    );
    vedlegg.forEach((v: VedleggWithFile) => {
        formData.append('vedlegg', v.fil, v.uploadId);
    });
    return formData;
};

export interface NextStepAction {
    type: SporsmalsflytActionKeys.NextStep;
    kundeinfo?: Kundeinfo;
}

export const nextStepAction = (kundeinfo?: Kundeinfo): NextStepAction => ({
    type: SporsmalsflytActionKeys.NextStep,
    kundeinfo,
});

export interface PreviousStepAction {
    type: SporsmalsflytActionKeys.PreviousStep;
}

export const previousStepAction = (): PreviousStepAction => ({
    type: SporsmalsflytActionKeys.PreviousStep,
});

export interface UpdateWithBesvarteSporsmalFromCacheAction {
    type: SporsmalsflytActionKeys.UpdateWithBesvarteSporsmalFromCacheAction;
    besvarteSporsmal: BesvartSporsmal[];
}

export const updateWithBesvarteSporsmalFromCacheAction = (
    besvarteSporsmal: BesvartSporsmal[],
): UpdateWithBesvarteSporsmalFromCacheAction => ({
    type: SporsmalsflytActionKeys.UpdateWithBesvarteSporsmalFromCacheAction,
    besvarteSporsmal,
});

export interface UpdateForsikringObjekterCheckboxAction {
    type: SporsmalsflytActionKeys.UpdateForsikringObjekterValg;
    valgtObjektnummer: number;
}

export const updateValgteObjekter = (valgtObjektnummer: number): UpdateForsikringObjekterCheckboxAction => ({
    type: SporsmalsflytActionKeys.UpdateForsikringObjekterValg,
    valgtObjektnummer,
});

export interface UpdateForsikringObjekterCheckboxWithObjekterAction {
    type: SporsmalsflytActionKeys.UpdateForsikringObjekterCheckboxWithObjekter;
    objekter: ObjektAlternativ[];
}

export const updateObjekterForForsikring = (
    objekter: ObjektAlternativ[],
): UpdateForsikringObjekterCheckboxWithObjekterAction => ({
    type: SporsmalsflytActionKeys.UpdateForsikringObjekterCheckboxWithObjekter,
    objekter,
});

const updateCheckedStatusForObjektWithObjektnummer = (
    currentSporsmal: CurrentForsikringObjekterCheckboxSporsmal,
    objektnummer: number,
): ObjektAlternativ[] => {
    return currentSporsmal.objekter.map((objekt) => {
        if (objekt.objektnummer === objektnummer) {
            return { ...objekt, checked: !objekt.checked };
        }
        return objekt;
    });
};

export const updateCurrentForsikringObjekterCheckbox = (
    currentSporsmal: CurrentSporsmal,
    objektnummer: number,
): CurrentSporsmal => {
    if (currentSporsmal.type === SporsmalType.ForsikringObjekterCheckbox) {
        return {
            ...currentSporsmal,
            objekter: updateCheckedStatusForObjektWithObjektnummer(currentSporsmal, objektnummer),
        };
    }

    return currentSporsmal;
};
